import { Fragment, useState, useEffect } from "react";
import { string, arrayOf, func, bool } from "prop-types";
import ReportItem from "../ReportItem/ReportItem.jsx";
import AnimateHeight from "react-animate-height";
import Button from "../../../Button/Button.jsx";
import Modal from "../../../../../../Common/js/components/Modal/Modal.jsx";
import TitleForm from "../TitleForm/TitleForm.jsx";
import { pageShape } from "../../ReportBuilderTypes.js";
import cx from "classnames";
import showFile from "../../../../common/showFile.js";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { getPdfStringFromUrl } from "../../../../common/constructPdfString.js";
import Checkbox from "../../../Checkbox/Checkbox.jsx";

const propTypes = {
  title: string.isRequired,
  type: string.isRequired,
  isAuthenticated: bool.isRequired,
  pages: arrayOf(pageShape).isRequired,
  onAddPages: func.isRequired,
  onRemoveReportItem: func.isRequired,
  onSaveDraftReport: func,
  onRemoveAllReportItems: func.isRequired,
  onUpdateTitle: func,
  onDeleteSavedReport: func,
  onDragEnd: func,
};

const Report = (props) => {
  const {
    title,
    type,
    isAuthenticated,
    pages,
    onAddPages,
    onRemoveReportItem,
    onSaveDraftReport,
    onRemoveAllReportItems,
    onUpdateTitle,
    onDeleteSavedReport,
    onDragEnd,
  } = props;

  const [isReportOpen, setIsReportOpen] = useState(
    type === "draftReport" ? true : false,
  );
  const [exportReport, setExportReport] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hideImages, setHideImages] = useState(false);

  useEffect(() => {
    if (exportReport) {
      setExportReport(false);

      const dataObj = {
        reportTitle: title,
        reportPages: pages.map((page) => {
          return { pageTitle: page.pageTitle, pageGuid: page.pageGuid };
        }),
      };

      const data = JSON.stringify(dataObj);

      setIsLoading(true);
      fetch("/api/ReportBuilder/StreamReportPdf", {
        method: "POST", // *GET, POST, PUT, DELETE, etc.
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        credentials: "same-origin", // include, *same-origin, omit
        headers: {
          "Content-Type": "application/json",
          // 'Content-Type': 'application/x-www-form-urlencoded',
        },
        redirect: "follow", // manual, *follow, error
        referrerPolicy: "no-referrer", // no-referrer, *client
        body: data, // body data type must match "Content-Type" header
      })
        .then((report) => report.blob())
        .then((blob) => {
          setIsLoading(false);
          showFile(blob, title);
        });
    }
  }, [exportReport, hideImages, pages, title]);

  const toggleHeight = (isOpen) => {
    setIsReportOpen(!isOpen);
  };

  const handleExport = (e) => {
    setExportReport(true);
  };

  const reportTitle =
    title && type === "savedReport" ? (
      <div className="report-title__container">
        <button
          className={cx("report__accordion", {
            "report-accordion--open": isReportOpen,
          })}
          onClick={() => toggleHeight(isReportOpen)}
          type="button"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="feather feather-chevron-right"
            style={{ color: "#0098a7", marginRight: "15px" }}
          >
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>

          <h2 className="report__title">{title}</h2>
        </button>
        <button
          onClick={() => setIsEditModalOpen(true)}
          type="button"
          className="report__edit-title"
        >
          Edit Title
        </button>
      </div>
    ) : (
      <h2 className="report__title">{title}</h2>
    );

  return (
    <div className="report">
      {reportTitle}
      <AnimateHeight height={isReportOpen ? "auto" : 0} duration={500}>
        <div className="report__labels">
          <div className="report__reorder">Reorder</div>
          <div className="report__section">Section</div>
          <div className="report__remove">Remove</div>
        </div>
        <div className="report__report-items">
          <ReportItem title="Cover" position="I" />
          <ReportItem title="Table of Contents" position="II" />
          <DragDropContext onDragEnd={(result) => onDragEnd(result)}>
            <Droppable droppableId={title}>
              {(droppableProvided) => (
                <div
                  {...droppableProvided.droppableProps}
                  ref={droppableProvided.innerRef}
                >
                  {pages &&
                    pages.map((page, index) => {
                      return (
                        <Draggable
                          key={page.pageGuid}
                          draggableId={page.pageGuid}
                          index={index}
                        >
                          {(draggableProvided) => (
                            <div
                              ref={draggableProvided.innerRef}
                              {...draggableProvided.draggableProps}
                              {...draggableProvided.dragHandleProps}
                            >
                              <ReportItem
                                guid={page.pageGuid}
                                index={index}
                                title={page.pageTitle}
                                onRemoveReportItem={() =>
                                  onRemoveReportItem(page)
                                }
                                breadcrumb={page.pageBreadcrumb}
                                position={index + 1}
                                movable={true}
                              />
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                  {droppableProvided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
        <div className="report__add-sections">
          <Button
            className="btn--primary-ghost report__add-sections-btn"
            onClick={onAddPages}
          >
            Add Sections
          </Button>
          <button
            onClick={onRemoveAllReportItems}
            className="report__remove-all"
            type="button"
          >
            Remove All
          </button>
        </div>
        <div className="report__save-export">
          {/* if the user is logged in  and the report type is draft*/}
          {type === "draftReport" && isAuthenticated && (
            <Fragment>
              <Button
                onClick={() => setIsSaveModalOpen(true)}
                className="btn--primary-ghost report__save"
              >
                Save Report
              </Button>
              <Button
                className="report__export-btn btn--primary"
                onClick={() => setIsExportModalOpen(true)}
              >
                Export
              </Button>
            </Fragment>
          )}

          {/* if the user is not logged in  and the report type is draft*/}
          {type === "draftReport" && !isAuthenticated && (
            <Button
              className="report__export-btn btn--primary"
              onClick={() => setIsPromptModalOpen(true)}
            >
              Export
            </Button>
          )}

          {/* if the the report type is saved the user is already logged in*/}
          {type === "savedReport" && (
            <Fragment>
              <div className="report__delete">
                <div>
                  <Button
                    className="report__delete-btn"
                    onClick={onDeleteSavedReport}
                  >
                    Delete Report
                  </Button>
                  <div className="report__delete-message">
                    This cannot be undone. You will lose all saved information.
                  </div>
                </div>
              </div>
              <Button
                className="report__export-btn btn--primary"
                onClick={() => setIsExportModalOpen(true)}
              >
                Export
              </Button>
            </Fragment>
          )}
        </div>
      </AnimateHeight>

      <Modal
        className="report-modal"
        isOpen={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
      >
        {isSaveModalOpen && (
          <TitleForm
            btnText="Save"
            onSubmit={(title) => onSaveDraftReport(title)}
            modalTitle="Save Report"
            initialReportTitle={title}
            onCancel={() => setIsSaveModalOpen(false)}
            type="edit/save"
          />
        )}
      </Modal>
      <Modal
        className="report-modal"
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
      >
        {isEditModalOpen && (
          <TitleForm
            btnText="Save"
            onSubmit={(title) => onUpdateTitle(title)}
            modalTitle="Edit Report Title"
            initialReportTitle={title}
            onCancel={() => setIsEditModalOpen(false)}
            type="edit/save"
          />
        )}
      </Modal>
      <Modal
        className="report-modal"
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
      >
        {isExportModalOpen ? (
          <Fragment>
            <TitleForm
              btnText="Export report"
              initialReportTitle="New Report"
              modalTitle="Export Report"
              type="export"
              onExport={handleExport}
            />

            {isLoading && (
              <div className="report__please-wait">
                <div>Please wait while your report is exported.</div>
                <img
                  src="/areas/EGH/assets/img/gif/loading-spinner.gif"
                  alt="loading spinner"
                />
              </div>
            )}
          </Fragment>
        ) : null}
      </Modal>
      <Modal
        className="report-modal"
        isOpen={isPromptModalOpen}
        onClose={() => setIsPromptModalOpen(false)}
      >
        {isPromptModalOpen ? (
          <div>
            <h2 className="title-form__title">Export Report</h2>
            <div className="report__login-signup">
              <span>Please </span>
              <button
                className="report__login js-global-modal-link"
                data-modal="login"
                type="button"
                onClick={() => {
                  setIsPromptModalOpen(false);
                  window.openGlobalModal("login");
                }}
              >
                login
              </button>{" "}
              or{" "}
              <button
                className="report__signup js-global-modal-link"
                data-modal="signup"
                type="button"
                onClick={() => {
                  setIsPromptModalOpen(false);
                  window.openGlobalModal("signup");
                }}
              >
                signup
              </button>{" "}
              to save and export reports.
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

Report.propTypes = propTypes;
export default Report;
